import { useEffect, useState } from 'react';
import type { ILanguageDto, ITourDto } from '../../model/interface';
import { IUserProperties } from '../../controller/interface/userstate';
import { injectAPISession, ISession } from '../interface';

function useActiveSession(): ISession | null {
    const sessionFetcher = injectAPISession();

    const [session, setSession] = useState<ISession | null>(sessionFetcher.active);

    useEffect(() => {
        const callback = (newValue: ISession | null) => {
            setSession(newValue);
        };

        sessionFetcher.subscribeActiveChanged(callback);

        return () => {
            sessionFetcher.unsubscribeActiveChanged(callback);
        };
    }, [sessionFetcher]);

    return session;
}

function _useActiveSessionUserStateValue<T>(
    session: ISession | null,
    key: string,
    defaultValue: T,
): T {
    const [saveVal, setSaveVal] = useState<T>(
        session?.state?.getPropertyWithDefault(key, defaultValue) ?? defaultValue,
    );

    useEffect(() => {
        const callback = (name: string, newValue: T) => {
            setSaveVal(newValue);
        };

        session?.state?.subscribeChanges(key, callback);

        return () => {
            session?.state.unsubscribeChanges(key, callback);
        };
    }, [session, key]);

    return saveVal;
}

function useActiveSessionLanguageId(): number {
    return _useActiveSessionUserStateValue(useActiveSession(), IUserProperties.LanguageId, -1);
}

function useActiveSessionTourId(): number {
    return _useActiveSessionUserStateValue(useActiveSession(), IUserProperties.TourId, -1);
}

function useActiveSessionLanguage(): ILanguageDto | null {
    const [saveVal, setSaveVal] = useState<ILanguageDto | null>(null);

    const session = useActiveSession();
    const languageId = _useActiveSessionUserStateValue(session, IUserProperties.LanguageId, -1);

    useEffect(() => {
        if (session && languageId && languageId > 0) {
            session.db
                .open()
                .then(db => db.get('ILanguageDto', languageId))
                .then(language => {
                    setSaveVal(language);
                    session.db.close();
                })
                .catch(error => {
                    throw error;
                });
        }
    }, [session, languageId]);

    return saveVal;
}

function useActiveSessionTour(): ITourDto | null {
    const [saveVal, setSaveVal] = useState<ITourDto | null>(null);

    const session = useActiveSession();
    const tourId = _useActiveSessionUserStateValue(session, IUserProperties.TourId, -1);

    useEffect(() => {
        if (session && tourId && tourId > 0) {
            session.db
                .open()
                .then(db => db.get('ITourDto', tourId))
                .then(tour => {
                    setSaveVal(tour);
                    session.db.close();
                })
                .catch(error => {
                    throw error;
                });
        }
    }, [session, tourId]);

    return saveVal;
}

const module = {
    useActiveSession,
    useActiveSessionLanguageId,
    useActiveSessionTourId,
    useActiveSessionLanguage,
    useActiveSessionTour,
};

export default module;
